import React, { useState, useEffect } from 'react';
import { FileText, File, Globe, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as api from '../../../api/educentialApi';

const OverseasDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.getOverseasDashboard();
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching overseas dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchDashboard}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Overseas Education</h1>
              <p className="text-gray-600 mt-1">Your journey to studying abroad starts here</p>
            </div>
            <Link
              to="/services"
              className="px-4 py-2 text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              ← Back to Services
            </Link>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            label="Applications"
            value={dashboardData?.applications?.length || 0}
            icon={FileText}
          />
          <StatCard
            label="Documents"
            value={dashboardData?.documents?.total || 0}
            icon={File}
          />
          <StatCard
            label="Countries"
            value={dashboardData?.countries?.length || 0}
            icon={Globe}
          />
          <StatCard
            label="Shortlisted"
            value={dashboardData?.shortlisted || 0}
            icon={Star}
          />
        </div>

        {/* Country-Specific Services */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Study Destinations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <CountryCard
              country="United Kingdom"
              description="Explore top UK universities and application requirements"
              path="/services/overseas/uk/pathfinder"
              features={['University Pathfinder', 'Financial Analysis', 'Readiness Assessment']}
            />
            <CountryCard
              country="United States"
              description="Navigate the US college application process"
              path="/services/overseas/us/pathfinder"
              comingSoon
              features={['University Finder', 'SAT/ACT Prep', 'Visa Guide']}
            />
            <CountryCard
              country="Canada"
              description="Apply to Canadian universities and colleges"
              path="/services/overseas/canada/pathfinder"
              comingSoon
              features={['Program Search', 'Express Entry', 'SDS Guide']}
            />
            <CountryCard
              country="Australia"
              description="Study in Australia's world-class institutions"
              path="/services/overseas/australia/pathfinder"
              comingSoon
              features={['University Match', 'Visa Process', 'Cost Calculator']}
            />
            <CountryCard
              country="Germany"
              description="Free education at German universities"
              path="/services/overseas/germany/pathfinder"
              comingSoon
              features={['Course Finder', 'Language Requirements', 'Blocked Account']}
            />
            <CountryCard
              country="Ireland"
              description="Study and work opportunities in Ireland"
              path="/services/overseas/ireland/pathfinder"
              comingSoon
              features={['University Guide', 'Work Permits', 'Application Timeline']}
            />
          </div>
        </div>

        {/* Recent Applications */}
        {dashboardData?.applications && dashboardData.applications.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Applications</h2>
            <div className="space-y-3">
              {dashboardData.applications.map((app) => (
                <div key={app.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{app.country} Application</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Created: {new Date(app.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon }) => {
  return (
    <div className="relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 overflow-hidden group">
      <div className="absolute inset-0 bg-teal-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="bg-teal-100 p-3 rounded-lg">
            <Icon className="w-6 h-6 text-teal-600" />
          </div>
        </div>
        <p className="text-sm text-gray-600 font-medium">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
    </div>
  );
};

// Country Card Component
const CountryCard = ({ country, description, path, comingSoon, features }) => {
  return (
    <div className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-teal-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="bg-teal-100 p-3 rounded-lg group-hover:bg-teal-600 transition-colors">
            <Globe className="w-8 h-8 text-teal-600 group-hover:text-white transition-colors" />
          </div>
          {comingSoon && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Coming Soon
            </span>
          )}
        </div>
        <h3 className="text-xl font-semibold text-gray-900 group-hover:text-teal-600 transition-colors mb-2">{country}</h3>
        <p className="text-sm text-gray-600 mb-4">{description}</p>

        {/* Features List */}
        <ul className="space-y-2 mb-4">
          {features.map((feature, idx) => (
            <li key={idx} className="text-sm text-gray-700 flex items-center">
              <svg className="w-4 h-4 text-teal-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>

        {comingSoon ? (
          <button
            disabled
            className="w-full py-2 px-4 rounded-lg font-medium bg-gray-200 text-gray-600 cursor-not-allowed"
          >
            Coming Soon
          </button>
        ) : (
          <Link
            to={path}
            className="block w-full py-2 px-4 rounded-lg font-medium bg-teal-600 text-white hover:bg-teal-700 text-center transition-colors"
          >
            Explore
          </Link>
        )}
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusColors = {
    DRAFT: 'bg-gray-100 text-gray-800',
    SUBMITTED: 'bg-blue-100 text-blue-800',
    UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
    OFFER_SECURED: 'bg-green-100 text-green-800',
    VISA_IN_PROGRESS: 'bg-purple-100 text-purple-800',
    COMPLETED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

export default OverseasDashboard;
